/**
 * Transforms raw WebSocket frames into heatmap-ready data.
 *
 * Builds a grid where:
 * - Y-axis = strike prices
 * - X-axis = expiration dates (like Heatseeker)
 * - Each cell = net exposure for that strike at that expiration
 *
 * Optional `expirationLimit` slices the X-axis to the nearest N
 * expirations (sorted ascending — front-week first). 0 = no limit.
 * The slicing happens after the cell matrix is built so column counts
 * always match `dates.length`. This is the lever the EXPS control in
 * HeatmapControls drives.
 */

import { useMemo } from 'react';
import { HeatmapFrame, ViewMode, Node } from '../lib/types';

export interface HeatmapCell {
  strike: number;
  date: string;
  value: number;
  node: Node | null;
}

export interface HeatmapData {
  cells: HeatmapCell[][];      // [row (strike)][col (date)]
  strikes: number[];            // Y-axis labels (sorted ascending)
  dates: string[];              // X-axis labels (expiration dates)
  maxAbsValue: number;          // Global max |value| (kept for fallback)
  /** Per-column (per-expiration) max |value|. Coloring each date column
   *  against its OWN ceiling stops a single 0DTE near-ATM cell from
   *  setting one global scale that washes every other column to a faint,
   *  target-less wash. Index aligns with `dates`. */
  colMaxAbs: number[];
  spotPrice: number;
  nodes: Node[];
}

/**
 * Look up a value from a string-keyed dict using a numeric strike.
 * Handles key format mismatch: backend may send "630.0" but JS has 630.
 */
function lookupStrike(data: Record<string, number>, strike: number): number {
  // Try exact match first
  const v1 = data[String(strike)];
  if (v1 !== undefined) return v1;
  // Try with .0 suffix
  const v2 = data[strike.toFixed(1)];
  if (v2 !== undefined) return v2;
  // Try parsing as float string
  const v3 = data[parseFloat(String(strike)).toString()];
  if (v3 !== undefined) return v3;
  return 0;
}

export function useHeatmapData(
  frame: HeatmapFrame | null,
  viewMode: ViewMode,
  expirationLimit: number = 0,
): HeatmapData | null {
  // Stable scalar key: re-run the heavy transform only when the frame
  // timestamp changes (new data from the pipeline) or when view controls
  // change.  Using the whole `frame` object reference as a dep causes a
  // re-run on every WS message even when the backend sends the same
  // logical snapshot (e.g. reconnect re-sends the cached frame).
  // `frame?.timestamp` is an ISO string stamped per pipeline run, so it
  // changes exactly when the data changes — ideal memo key.
  const frameKey = frame?.timestamp ?? null;

  return useMemo(() => {
    if (!frame) return null;

    const nodes = viewMode === 'gex' ? frame.gex_nodes : frame.vex_nodes;
    const byExpiration = viewMode === 'gex'
      ? frame.gex_by_expiration
      : frame.vex_by_expiration;

    // Use expiration dates as columns (sorted ascending — nearest
    // first). Apply user's display limit before building the matrix:
    // limiting at the dates array means we don't compute cell values
    // for hidden columns, which keeps the table render light when a
    // user picks "3" with 12 expirations available upstream.
    const allDates = Object.keys(byExpiration || {}).sort();
    const dates = expirationLimit > 0 ? allDates.slice(0, expirationLimit) : allDates;

    if (dates.length === 0) {
      // No per-expiration data yet (waiting for first frame). Render
      // an empty heatmap instead of trying to fall back to the aggregate
      // dicts — those are stripped from the WS payload now.
      return null;
    }

    // Collect strike keys directly from the per-expiration data the
    // wire already carries. Previously we also unioned with
    // gex_by_strike / vex_by_strike, but those are now stripped from
    // the WS payload (see api/ws.py::slim_for_wire) — and the union
    // was redundant in practice since every traded strike shows up
    // in at least one expiration's data.
    //
    // Strike-participation filter: only keep strikes traded in at
    // least PARTICIPATION_FLOOR of the displayed expirations.
    // Without this, NDX/SPX (30+ expirations, sparse strike overlap
    // because each weekly carries a different subset of strikes)
    // render with rows of cells that paint as "value=0" simply
    // because the contract doesn't exist in that column. The user
    // reads that as an empty heatmap.
    //
    // Dynamic floor: for dense index chains (many expirations) we keep
    // the 30% floor so the grid stays clean. For sparse single-name
    // tickers (few expirations, irregular strike ladders) we drop the
    // floor to 10% so strikes that only appear in 1-2 expirations still
    // show. The threshold is ≤6 displayed expirations — NVDA/AAPL/AMD
    // typically have 4-8 active expirations, SPX/NDX have 20-30+.
    //
    // King/Counter-King/Gatekeeper strikes are force-kept regardless of
    // participation so analytical anchors never disappear.
    const FLOOR_DENSE  = 0.30;   // index chains: 20+ exps
    const FLOOR_SPARSE = 0.10;   // single-names: ≤8 exps
    const SPARSE_THRESHOLD = 8;
    const PARTICIPATION_FLOOR = dates.length <= SPARSE_THRESHOLD ? FLOOR_SPARSE : FLOOR_DENSE;
    const minExpirations = Math.max(1, Math.ceil(dates.length * PARTICIPATION_FLOOR));
    const participation = new Map<string, number>();
    for (const date of dates) {
      const expData = byExpiration[date] || {};
      for (const [k, v] of Object.entries(expData)) {
        if (v !== 0) {
          participation.set(k, (participation.get(k) ?? 0) + 1);
        }
      }
    }
    const protectedStrikes = new Set(nodes.map(n => n.strike));
    const strikeKeySet = new Set<string>();
    for (const date of dates) {
      const expData = byExpiration[date] || {};
      for (const k of Object.keys(expData)) {
        const count = participation.get(k) ?? 0;
        const numericStrike = parseFloat(k);
        const isProtected = !isNaN(numericStrike) && protectedStrikes.has(numericStrike);
        if (count >= minExpirations || isProtected) {
          strikeKeySet.add(k);
        }
      }
    }

    // Parse to numbers and sort. Keep all strikes within ±40% of spot —
    // this matches the backend wire band so nothing is silently dropped
    // here that was already sent. The applyWindow() call in HeatmapTable /
    // HeatmapCanvas then narrows to ±15 / ±30 / ALL per the user's choice.
    //
    // Previous value was ±5% (spotPrice * 0.05). NVDA @ $221 → only strikes
    // from $210 to $232 survived, which is ~4-5 strikes with $5 spacing.
    // That's why "10 strikes total no matter the option chosen" — the window
    // control (±15/±30/ALL) was slicing an already-4-strike dataset.
    const spotPrice = frame.spot_price;
    const RANGE_BAND = 0.40;   // match backend _STRIKE_BAND; applyWindow does the UI trim
    const allStrikes = Array.from(strikeKeySet)
      .map(k => parseFloat(k))
      .filter(s => !isNaN(s) && s >= spotPrice * (1 - RANGE_BAND) && s <= spotPrice * (1 + RANGE_BAND))
      .sort((a, b) => a - b);

    // Deduplicate (in case "630" and "630.0" both parsed to 630)
    const strikes = [...new Set(allStrikes)];

    // Build node lookup by strike value
    const nodeMap = new Map<number, Node>();
    nodes.forEach(n => { nodeMap.set(n.strike, n); });

    // Build cell matrix. Track BOTH a global max (fallback) and a
    // per-column max so each expiration can be colored on its own scale.
    let maxAbsValue = 0;
    const colMaxAbs: number[] = new Array(dates.length).fill(0);

    const cells: HeatmapCell[][] = strikes.map(strike => {
      return dates.map((date, colIdx) => {
        const expData = byExpiration[date] || {};
        const value = lookupStrike(expData, strike);

        const absV = Math.abs(value);
        maxAbsValue = Math.max(maxAbsValue, absV);
        colMaxAbs[colIdx] = Math.max(colMaxAbs[colIdx], absV);

        // Find node — try exact match and nearby (within 0.5)
        let node = nodeMap.get(strike) || null;
        if (!node) {
          for (const [ns, n] of nodeMap) {
            if (Math.abs(ns - strike) < 0.5) {
              node = n;
              break;
            }
          }
        }

        return {
          strike,
          date,
          value,
          node,
        };
      });
    });

    return {
      cells,
      strikes,
      dates,
      maxAbsValue,
      colMaxAbs,
      spotPrice,
      nodes,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameKey, viewMode, expirationLimit]);
}

// (Previously had a buildSingleColumnData fallback that consumed
// gex_by_strike / vex_by_strike for an empty-expiration edge case.
// Both fields are now stripped from WS payloads (api/ws.py
// slim_for_wire) so the fallback would never fire on real data —
// removed to keep the hook lean. Empty-frame state returns null;
// the calling component renders <HeatmapEmpty/>.)
